import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { Store } from "@ngrx/store";
import { isReplete } from "@nll/datum/Datum";
import { isFailure } from "@nll/datum/DatumEither";
import { Subscription } from "rxjs";
import { delay, map, first } from "rxjs/operators";
import {
  clearEditVehicleIdentifier,
  editVehicleIdentifier,
  getVehicles,
  selectEditVehicleIdentifier,
  selectVehicles,
  selectVehiclesWithTemporaryIdentifier,
} from "src/app/core/ngrx/myqq";
import {
  selectUpdateFlockPresaleVehicleStep,
  setShowFlockPresaleModal,
  setUpdateFlockPresaleVehicleStep,
  UpdateFlockPresaleVehicleSteps,
} from "src/app/core/ngrx/ui";
import { Vehicle } from "src/app/core/services/myqq";
import { filterSuccessMapToRightValue } from "src/lib/datum-either";
import { combineS } from "src/lib/datum-either";

@Component({
  templateUrl: "./update-flock-presale-vehicle.page.html",
})
export class UpdateFlockPresaleVehiclePage implements OnInit {
  readonly currentStep$ = this.store$.select(
    selectUpdateFlockPresaleVehicleStep
  );
  readonly steps = UpdateFlockPresaleVehicleSteps;
  readonly flockPresaleVehicles$ = this.store$.select(
    selectVehiclesWithTemporaryIdentifier
  );
  readonly contextData$ = combineS({
    flockPresaleVehicles: this.flockPresaleVehicles$,
    vehicles: this.store$.select(selectVehicles),
  });
  readonly editVehicleIdentifier$ = this.store$.select(
    selectEditVehicleIdentifier
  );
  private subs: Subscription[] = [];
  vehicleCount = 1;
  totalPresaleVehicleCount: Number;
  error: string = null;
  @ViewChild("footer", { static: false }) private _footerTemplate: TemplateRef<
    any
  >;
  readonly footerTemplate = this.contextData$.pipe(
    delay(1),
    map(() => this._footerTemplate)
  );

  constructor(
    readonly store$: Store<any>,
    public dialogRef: MatDialogRef<UpdateFlockPresaleVehiclePage>
  ) {}

  ngOnInit() {
    this.handleGetData();
    this.handleNoFlockPresaleVehicles();
  }

  ngOnDestroy(): void {
    this.handleReset();
    this.subs?.forEach((s) => s.unsubscribe());
    this.closeModal();
  }

  handleDismissErrorBanner() {
    this.error = null;
    this.store$.dispatch(clearEditVehicleIdentifier(null));
  }

  handleGetData() {
    this.store$.dispatch(getVehicles.pending(null));
  }

  handleNoFlockPresaleVehicles() {
    this.subs.push(
      this.flockPresaleVehicles$
        .pipe(filterSuccessMapToRightValue, first())
        .subscribe((val) => {
          this.totalPresaleVehicleCount = val.length;
          if (val?.length <= 0) {
            this.closeModal();
          }
        })
    );
  }

  handleShowEdit(startFlow: boolean) {
    return startFlow
      ? this.store$.dispatch(setUpdateFlockPresaleVehicleStep(this.steps.edit))
      : this.closeModal();
  }

  closeModal() {
    this.dialogRef.close();
    this.store$.dispatch(setShowFlockPresaleModal(false));
  }

  handleSubmit(vehicle: Vehicle, vehicles: Vehicle[], numPresalesVehicle) {
    if (!!this.getExistingVehicleOnAccountWithMembership(vehicle, vehicles)) {
      this.error =
        "This vehicle is on your account and already has a membership. Please try a different vehicle.";
    } else {
      this.store$.dispatch(editVehicleIdentifier.pending(vehicle));
      this.subs.push(
        this.editVehicleIdentifier$.pipe(first(isReplete)).subscribe((res) => {
          if (isFailure(res)) {
            this.error = "We are having problems updating your vehicle.";
          } else if (numPresalesVehicle == 1) {
            this.handleReset();
            this.closeModal();
          } else {
            this.vehicleCount++;
            this.store$.dispatch(clearEditVehicleIdentifier(null));
          }
        })
      );
    }
  }

  handleReset() {
    this.store$.dispatch(clearEditVehicleIdentifier(null));
    this.store$.dispatch(setUpdateFlockPresaleVehicleStep(this.steps.alert));
  }

  handleCancel() {
    this.handleReset();
    this.closeModal();
  }

  getExistingVehicleOnAccountWithMembership(newVehicle, vehicles): Vehicle {
    return vehicles.find((existingVehicle) => {
      return (
        existingVehicle.hasMembership &&
        !existingVehicle.isTemporaryIdentifier &&
        this.isSameLPVin(newVehicle, existingVehicle)
      );
    });
  }

  isSameLPVin(newVehicle, oldVehicle): boolean {
    if (!!oldVehicle.state && !!oldVehicle.license && !!oldVehicle.vin) {
      return (
        oldVehicle.state?.toLowerCase() === newVehicle.state?.toLowerCase() &&
        oldVehicle.license?.toLowerCase() ===
          newVehicle.license?.toLowerCase() &&
        oldVehicle.vin?.toLowerCase() === newVehicle.vin?.toLowerCase()
      );
    }
    if (!!oldVehicle.state && !!oldVehicle.license) {
      return (
        oldVehicle.state?.toLowerCase() === newVehicle.state?.toLowerCase() &&
        oldVehicle.license?.toLowerCase() === newVehicle.license?.toLowerCase()
      );
    }
    if (!!oldVehicle.vin) {
      return oldVehicle.vin?.toLowerCase() === newVehicle.vin?.toLowerCase();
    }
    return false;
  }
}
